<template>
  <div>
    <!-- ── ESTADO: Esperando escáner ─────── -->
    <div v-if="estado === 'esperando'" class="text-center py-4">
      <v-icon size="80" color="primary" class="mb-3">mdi-qrcode-scan</v-icon>
      <h3 class="text-h6 mb-2">Escáner de QR</h3>
      <p class="text--secondary text-body-2 mb-4">
        Apunta la cámara al código QR del carnet del miembro
      </p>
      <div id="reader" class="mx-auto rounded-xl" style="max-width:340px;overflow:hidden" />
      <v-btn text small color="error" class="mt-3" @click="detenerEscaner">
        <v-icon left small>mdi-close</v-icon> Cancelar
      </v-btn>
    </div>

    <!-- ── ESTADO: Cargando perfil ─────── -->
    <div v-if="estado === 'cargando'" class="text-center py-8">
      <v-progress-circular size="56" indeterminate color="primary" />
      <p class="mt-4 text--secondary">Verificando matrícula {{ matriculaLeida }}…</p>
    </div>

    <!-- ── ESTADO: Perfil encontrado ─────── -->
    <div v-if="estado === 'perfil' && miembro">
      <!-- Tarjeta del miembro -->
      <v-card
        rounded="xl" class="overflow-hidden"
        :color="miembro.estado === 'ACTIVO' ? '' : 'red lighten-5'"
      >
        <div
          class="pa-4 d-flex align-center"
          :style="{ background: miembro.estado === 'ACTIVO'
            ? 'linear-gradient(135deg,#1de9b6,#1565c0)'
            : 'linear-gradient(135deg,#ff5252,#b71c1c)' }"
        >
          <v-list-item-avatar size="60" class="mr-3 elevation-4">
            <v-img :src="imgUrl(miembro.imagen)" />
          </v-list-item-avatar>
          <div>
            <div class="white--text text-h6 font-weight-bold">{{ miembro.nombre }}</div>
            <div class="white--text text-caption">Matrícula: {{ miembro.matricula }}</div>
          </div>
          <v-spacer />
          <div class="text-right">
            <v-chip dark :color="miembro.estado === 'ACTIVO' ? 'success' : 'error'" small>
              {{ miembro.estado }}
            </v-chip>
          </div>
        </div>

        <v-card-text class="py-3">
          <v-row dense>
            <v-col cols="6">
              <div class="text-caption text--secondary">Plan</div>
              <div class="font-weight-medium">{{ miembro.membresia || 'Sin plan' }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption text--secondary">Días restantes</div>
              <div :class="diasColor + '--text'" class="font-weight-bold text-h6">
                {{ diasRestantes !== null ? diasRestantes : '—' }}
              </div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption text--secondary">Vence</div>
              <div class="font-weight-medium">{{ miembro.fechaFin || '—' }}</div>
            </v-col>
            <v-col cols="6">
              <div class="text-caption text--secondary">Teléfono</div>
              <div class="font-weight-medium">{{ miembro.telefono || '—' }}</div>
            </v-col>
          </v-row>
        </v-card-text>

        <!-- Alerta si vencido -->
        <v-alert v-if="miembro.estado !== 'ACTIVO'" type="error" dense class="ma-3 mt-0">
          ⚠️ Membresía vencida — No puede ingresar
        </v-alert>
        <v-alert v-else-if="diasRestantes !== null && diasRestantes <= 5 && diasRestantes > 0"
                 type="warning" dense class="ma-3 mt-0">
          ⏰ Vence en {{ diasRestantes }} día(s) — Recomendar renovación
        </v-alert>
      </v-card>

      <!-- Acciones -->
      <v-row class="mt-3" dense>
        <v-col cols="12" v-if="miembro.estado === 'ACTIVO'">
          <v-btn
            block color="success" rounded large :loading="registrando"
            elevation="3" @click="registrarVisita"
          >
            <v-icon left>mdi-check-circle</v-icon>
            Registrar Visita
          </v-btn>
        </v-col>
        <v-col cols="6">
          <v-btn block outlined rounded @click="reiniciar">
            <v-icon left small>mdi-qrcode-scan</v-icon> Nuevo
          </v-btn>
        </v-col>
        <v-col cols="6">
          <v-btn block outlined rounded color="primary" :to="'/editar-miembro/' + miembro.id">
            <v-icon left small>mdi-account-edit</v-icon> Perfil
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <!-- ── ESTADO: Visita registrada ─────── -->
    <div v-if="estado === 'exito'" class="text-center py-6">
      <v-icon size="72" color="success" class="mb-3">mdi-check-circle</v-icon>
      <h3 class="text-h6 success--text mb-1">¡Visita registrada!</h3>
      <p class="text--secondary">{{ miembro ? miembro.nombre : '' }}</p>
      <v-btn color="primary" rounded class="mt-3" @click="reiniciar">
        <v-icon left>mdi-qrcode-scan</v-icon> Escanear otro
      </v-btn>
    </div>

    <!-- ── ESTADO: Error ─────── -->
    <div v-if="estado === 'error'" class="text-center py-6">
      <v-icon size="72" color="error" class="mb-3">mdi-alert-circle</v-icon>
      <h3 class="text-h6 error--text mb-1">{{ errorMsg }}</h3>
      <v-btn outlined rounded color="primary" class="mt-3" @click="reiniciar">
        Intentar de nuevo
      </v-btn>
    </div>
  </div>
</template>

<script>
import HttpService from '../../Servicios/HttpService'

export default {
  name: 'EscanerQR',
  props: {
    idUsuario: { type: Number, required: true },
  },

  data() {
    return {
      estado: 'esperando',  // esperando | cargando | perfil | exito | error
      miembro: null,
      matriculaLeida: '',
      registrando: false,
      errorMsg: '',
      scanner: null,
    }
  },

  computed: {
    diasRestantes() {
      // Usar el valor calculado por el servidor (evita problemas de timezone)
      if (this.miembro && this.miembro.diasRestantes !== undefined && this.miembro.diasRestantes !== null) {
        return parseInt(this.miembro.diasRestantes)
      }
      if (!this.miembro || !this.miembro.fechaFin) return null
      var parts = this.miembro.fechaFin.split('-')
      var fin   = new Date(parseInt(parts[0]), parseInt(parts[1])-1, parseInt(parts[2]))
      var hoy   = new Date()
      hoy.setHours(0,0,0,0)
      return Math.floor((fin - hoy) / (1000 * 60 * 60 * 24))
    },
    diasColor() {
      const d = this.diasRestantes
      if (d === null || d <= 0) return 'error'
      if (d <= 5)               return 'orange'
      return 'success'
    },
  },

  mounted() {
    this.iniciarEscaner()
  },

  beforeDestroy() {
    this.detenerEscaner()
  },

  methods: {
    imgUrl(img) {
      return HttpService.getImgUrl(img)
    },

    iniciarEscaner() {
      var self = this
      this.estado = 'esperando'

      // Carga dinámica de html5-qrcode para no bloquear bundle
      import('html5-qrcode').then(function(mod) {
        var Html5Qrcode = mod.Html5Qrcode || mod.default
        self.scanner = new Html5Qrcode('reader')
        self.scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          function(texto) {
            // QR detectado
            self.detenerEscaner()
            self.procesarQR(texto.trim())
          },
          function() { /* error de frame, ignorar */ }
        ).catch(function(err) {
          self.estado = 'error'
          self.errorMsg = 'No se pudo acceder a la cámara: ' + err
        })
      })
    },

    detenerEscaner() {
      if (this.scanner) {
        this.scanner.stop().catch(function() {})
        this.scanner = null
      }
    },

    procesarQR(matricula) {
      this.matriculaLeida = matricula
      this.estado = 'cargando'
      var self    = this

      HttpService.registrar({
        metodo:    'buscar_por_qr',
        matricula: matricula,
      }, 'miembros.php').then(function(r) {
        if (!r || !r.ok) {
          self.estado   = 'error'
          self.errorMsg = r ? r.error : 'Miembro no encontrado'
          return
        }
        self.miembro = r.miembro
        self.estado  = 'perfil'
      }).catch(function() {
        self.estado   = 'error'
        self.errorMsg = 'Error de conexión'
      })
    },

    registrarVisita() {
      var self    = this
      var m       = this.miembro
      if (!m || m.estado !== 'ACTIVO') return
      this.registrando = true

      HttpService.registrar({
        metodo:      'registrar',
        visita: {
          idMiembro:   m.id,
          idUsuario:   this.idUsuario,
          idMembresia: m.idMembresia || null,
        },
      }, 'visitas.php').then(function() {
        self.registrando = false
        self.estado      = 'exito'
        self.$emit('visita-registrada', m)
      }).catch(function() {
        self.registrando = false
        self.estado      = 'error'
        self.errorMsg    = 'No se pudo registrar la visita'
      })
    },

    reiniciar() {
      this.miembro      = null
      this.errorMsg     = ''
      this.matriculaLeida = ''
      this.$nextTick(() => this.iniciarEscaner())
    },
  },
}
</script>



// WEBPACK FOOTER //
// src/components/Visitas/EscanerQR.vue